import { useGetGoogleMapApiKey } from '../../api/googlemapapikey';
import { useState, useEffect } from 'react';

// Global state to track if Google Maps API is loaded.
//
// readyCallbacks holds setIsReady functions from every hook instance that
// mounted while the API was still loading. They are all fired together
// when the API ready-callback runs, so consumers that mount during the
// loading window get notified along with the one that triggered the load.
let isApiLoaded = false;
let isApiLoading = false;
let readyCallbacks = [];

// Globally-named function that the Maps script invokes via &callback= when
// all libraries listed in &libraries= are ready. Required because:
//   - Without loading=async, script.onload IS the ready signal — but Google
//     warns about that pattern in the console.
//   - With loading=async, script.onload only means "bootstrap loaded" —
//     libraries are NOT yet on window.google.maps.{Map,marker,...}.
//   - The documented ready signal for the script-tag URL pattern is the
//     &callback= URL parameter. (The importLibrary() API exists only when
//     the inline-bootstrap-loader IIFE is used instead of a script tag.)
//
// Defined at module load so the URL `&callback=__livisiblyMapsReady` always
// resolves regardless of when the first hook instance runs.
window.__livisiblyMapsReady = () => {
  isApiLoaded = true;
  isApiLoading = false;
  readyCallbacks.forEach((cb) => cb());
  readyCallbacks = [];
};

// Fallback Map ID for AdvancedMarkerElement when the secret has not been
// populated with a real Map ID yet (e.g., a fresh env after CI/CD rollout
// before someone updates Secrets Manager). DEMO_MAP_ID is Google's
// documented placeholder — works for development but is rate-limited and
// not appropriate for production. Real per-env Map IDs are configured in
// Google Cloud Console (Maps Platform → Map Management) and stored in the
// livisibly/google_maps_api secret as the MAP_ID field.
const FALLBACK_MAP_ID = 'DEMO_MAP_ID';

export function useGoogleMapsApi() {
  const [isReady, setIsReady] = useState(isApiLoaded);
  const { data: apiKeyData } = useGetGoogleMapApiKey(true);
  const apiKey = apiKeyData?.KEY;
  const mapId = apiKeyData?.MAP_ID || FALLBACK_MAP_ID;

  useEffect(() => {
    // If API is already loaded, set ready state immediately
    if (isApiLoaded) {
      setIsReady(true);
      return;
    }

    // No API key yet — wait for the SWR fetch to populate it.
    if (!apiKey) return;

    // Register THIS hook instance for notification. Always do this before
    // checking isApiLoading so a second consumer that mounts during the
    // loading window doesn't get stranded waiting for an event that
    // already fired.
    const notify = () => setIsReady(true);
    readyCallbacks.push(notify);

    if (!isApiLoading) {
      isApiLoading = true;

      // Load Google Maps API.
      //
      // - libraries=marker required for AdvancedMarkerElement (the legacy
      //   google.maps.Marker is deprecated since 2024-02-21).
      // - libraries=geometry required for SymbolPath / Polyline icons used
      //   in trip-route curves.
      // - libraries=places required for the Place class — we resolve
      //   stored Google place_ids to lat/lng at render time via
      //   Place.fetchFields() rather than reading the cached coordinates
      //   from the geocode payload. This sidesteps the Google ToS
      //   §B.5.3 30-day cap on persisted lat/lng (we display fresh
      //   coords every render, even though the same coords are stored
      //   server-side as internal metadata).
      // - loading=async silences the "loaded directly without loading=async"
      //   console warning and is Google's recommended pattern.
      // - callback=__livisiblyMapsReady is the documented ready signal —
      //   Google calls this function when all listed libraries are loaded.
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=marker,geometry,places&loading=async&callback=__livisiblyMapsReady`;
      script.async = true;
      script.defer = true;

      script.onerror = () => {
        console.error('Failed to load Google Maps API');
        isApiLoading = false;
        // Drain pending callbacks so they don't leak; consumers will
        // remain isReady=false but at least won't queue indefinitely.
        readyCallbacks = [];
      };

      document.head.appendChild(script);
    }

    // Cleanup: if this consumer unmounts before the ready-callback fires,
    // remove its notify so we don't call setState on an unmounted component.
    return () => {
      readyCallbacks = readyCallbacks.filter((cb) => cb !== notify);
    };
  }, [apiKey]);

  return {
    isReady,
    apiKey,
    mapId,
  };
}

// Helper to extract the Google place_id from the stored geocode field.
//
// Both supported geocode shapes carry place_id at the top level:
//   - Slim (current writer): { place_id, lat, lng, _geocoded_at, _shape }
//   - Legacy Google full-payload (pre-AWS-migration writes):
//     { place_id, geometry: { location: { lat, lng } }, ... }
//
// The frontend uses ONLY this place_id for map rendering — we do NOT
// read the cached lat/lng for display. Instead, the maps loader
// constructs a google.maps.places.Place({ id: place_id }) and calls
// fetchFields({ fields: ['location'] }) at render time to get fresh
// coordinates. This gives us indefinite storage on the cheap (Google
// ToS §A.3 explicitly allows place_id storage forever) without
// needing to comply with the §B.5.3 30-day cap on lat/lng caching at
// the user-visible surface.
//
// Returns null for marker shapes (no place_id field), missing
// geocodes, malformed JSON, or AWS-PlaceId-shape data left over from
// the brief AWS Location experiment (those values won't resolve via
// Google's Place class — caller will see a fetchFields rejection
// and render no map, which is the expected behavior for unrenderable
// data).
export function extractPlaceId(geocodeString) {
  if (!geocodeString) return null;

  try {
    const data = JSON.parse(geocodeString);
    if (data && typeof data.place_id === 'string' && data.place_id) {
      return data.place_id;
    }
    return null;
  } catch (error) {
    console.error('Error parsing geocode data:', error);
    return null;
  }
}